import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountActions } from '@app/core/actions';
import * as fromRoot from '@app/reducers';
import { DialogRef } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

// Errors from Directory.createToken that the user cannot resolve by re-typing the password in this
// modal (an MFA challenge, or a revoked / disabled / absent identity). For these we abort to /signin
// instead of showing a misleading "wrong password" inline error.
const NON_RETRYABLE_ERROR_NAMES = ['MfaRequiredError', 'PasswordRevokedError', 'IdentityDisabledError', 'NotFoundError'];

// How long the success state ("you are signed back in") stays on screen before the modal auto-closes.
const SUCCESS_AUTO_CLOSE_MS = 1500;

@Component({
  selector: 'app-modal-relogin',
  templateUrl: './modal-relogin.component.html',
  styleUrls: ['./modal-relogin.component.scss'],
})
export class ModalReloginComponent implements OnDestroy {

  reloginForm: FormGroup;
  submitted = false;
  processing$: Observable<boolean>;
  // Success state: drives the "you are signed back in" confirmation view before the modal closes.
  succeeded$: Observable<boolean>;
  error$: Observable<{ name: string; message: string } | null>;
  // The i18n key to show for the current error: a wrong password vs a generic failure (network /
  // server / unknown), so a flaky-network user is not misleadingly told their password is wrong.
  errorMessageKey$: Observable<string | null>;
  username: string;

  private subscriptions: Subscription[] = [];
  private autoCloseTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private store$: Store<fromRoot.State>,
    private fb: FormBuilder,
    public ref: DialogRef,
  ) {
    this.username = this.ref.data?.username;
    this.processing$ = this.store$.select(fromRoot.getReloginProcessing);
    this.succeeded$ = this.store$.select(fromRoot.getReloginComplete);
    this.error$ = this.store$.select(fromRoot.getReloginError);
    this.errorMessageKey$ = this.error$.pipe(map(error => {
      if (!error) {
        return null;
      }
      return error.name === 'InvalidCredentialsError' ? 'MODAL_RELOGIN_ERROR_WRONG_PASSWORD' : 'MODAL_RELOGIN_ERROR_GENERIC';
    }));
    this.reloginForm = this.fb.group({
      password: ['', Validators.required],
    });
    this.subscriptions.push(
      // Re-login succeeded: a fresh session is persisted. Show the success state briefly (the
      // template switches to it via succeeded$), then auto-close so the user gets an explicit
      // "it worked" acknowledgement instead of the modal vanishing silently. They can also close now.
      this.succeeded$.subscribe((complete) => {
        if (complete && this.autoCloseTimeout === undefined) {
          this.autoCloseTimeout = setTimeout(() => this.close(), SUCCESS_AUTO_CLOSE_MS);
        }
      }),
      // A non-retryable failure (MFA, revoked/disabled/absent identity): no path forward here, so
      // clear the dead session and route to /signin rather than trapping the user in the modal.
      this.error$.subscribe((error) => {
        if (error && NON_RETRYABLE_ERROR_NAMES.includes(error.name)) {
          this.abort();
        }
      }),
    );
  }

  get f() { return this.reloginForm.controls; }

  onSubmit() {
    this.submitted = true;
    if (!this.reloginForm.valid) {
      return;
    }
    this.store$.dispatch(AccountActions.Relogin({ username: this.username, password: this.f.password.value }));
  }

  abort() {
    this.store$.dispatch(AccountActions.ReloginAbort());
    this.ref.close(false);
  }

  close() {
    if (this.autoCloseTimeout !== undefined) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = undefined;
    }
    this.ref.close(true);
  }

  ngOnDestroy() {
    if (this.autoCloseTimeout !== undefined) {
      clearTimeout(this.autoCloseTimeout);
    }
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
